import * as React from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { UserEntityDetails } from '../../axiosApi/models';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import intl from 'components/i18n/ReactIntlWrapper';
import { getUserEntity } from 'common/UserEntityProvider';
import { handleAPIError } from '../../common/errorHandler';
import { AssignmentAdd, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import Title from 'components/common/Title';
import { LineChartWrapper } from '../chart/LineChartWrapper';
import { lineElementClasses } from '@mui/x-charts/LineChart';
import { ReportsFilterUtilization, ReportsFilterUtilizationProps } from './ReportsFilterUtilization';
import { Chart, useChart } from '../chart';
import { ACCESSIBLE_COLORS } from '../../common/constants';

interface AllocationData {
    date: string;
    allocationPercentage: number;
}

interface ProjectData {
    idProject: number;
    projectName: string;
    allocations: AllocationData[];
}

interface EmployeeData {
    idEmployee: number;
    employeeName: string;
    projects: ProjectData[];
}

interface ApiResponse {
    data: EmployeeData[];
    errors: any;
}

type UtilizationReportComponentProps = {
    client?: boolean;
}

export const UtilizationReportComponent = (props: UtilizationReportComponentProps) => {
    const { client } = props;
    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const [loading, setLoading] = React.useState<Boolean>(false);
    const [errors, setErrors] = React.useState({});
    const [employeeData, setEmployeeData] = React.useState<EmployeeData[]>([]);
    const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>([]);
    const [expandedEmployees, setExpandedEmployees] = React.useState<number[]>([]);
    const [projectColorCache, setProjectColorCache] = React.useState<{ [key: string]: string }>({});
    const userEntity: UserEntityDetails = getUserEntity();

    const fetchEmployeeAllocationReport = async (filterData: ReportsFilterUtilizationProps) => {
        setLoading(true);
        try {
            let employeeIds = filterData.employees
                ? filterData.employees.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
                : [];

            // Fetch all active employees to ensure we only work with active employee IDs
            const employeeResponse = await api.employeeApi.apiVversionListEmployeesGet("1", false, {});
            
            const activeEmployeesList: any = employeeResponse.data?.data?.queryResult;
            const activeEmployeeIds = Array.isArray(activeEmployeesList) 
                ? activeEmployeesList.map((emp) => Number(emp.id))
                : [];

            // If no employees are selected, use all active employees
            if (employeeIds.length === 0) {
                if (!filterData.from || !filterData.to) {
                    setEmployeeData([]);
                    setSelectedEmployees([]);
                    setExpandedEmployees([]);
                    setLoading(false);
                    return;
                }
                
                employeeIds = activeEmployeeIds;
                
                if (employeeIds.length === 0) {
                    setEmployeeData([]);
                    setSelectedEmployees([]);
                    setExpandedEmployees([]);
                    setLoading(false);
                    return;
                }
            } else {
                // Filter selected employees to only include active ones
                employeeIds = employeeIds.filter(id => activeEmployeeIds.includes(id));
                
                if (employeeIds.length === 0) {
                    setEmployeeData([]);
                    setSelectedEmployees([]);
                    setExpandedEmployees([]);
                    setLoading(false);
                    return;
                }
            }

            // Check dates are valid
            if (!filterData.from || !filterData.to) {
                setEmployeeData([]);
                setSelectedEmployees([]);
                setExpandedEmployees([]);
                setLoading(false);
                return;
            }

            const response = await api.reportsApi.apiVversionEmployeeAllocationReportGetRangeGet(
                "1", // version
                filterData.from.toISOString().split('T')[0], // startDate
                filterData.to.toISOString().split('T')[0], // endDate
                employeeIds // employeeIds - always pass the array since we checked it's not empty
            );

            if (response.data.data) {
                const apiResponse = response.data.data as EmployeeData[];
                setEmployeeData(apiResponse);
                const uniqueEmployeeIds = apiResponse.map(emp => emp.idEmployee);
                setSelectedEmployees(uniqueEmployeeIds);
                if (uniqueEmployeeIds.length > 0) {
                    setExpandedEmployees([uniqueEmployeeIds[0]]);
                }
            } else {
                setEmployeeData([]);
                setSelectedEmployees([]);
                setExpandedEmployees([]);
            }
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            setEmployeeData([]);
            setSelectedEmployees([]);
            setExpandedEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterData: ReportsFilterUtilizationProps) => {
        fetchEmployeeAllocationReport(filterData);
    };

    const handleEmployeeToggle = (employeeId: number) => {
        setExpandedEmployees(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const getRandomColor = () => {
        const hue = Math.floor(Math.random() * 360);
        const pastel = `hsl(${hue}, 70%, 70%)`;
        const hslToHex = (h: number, s: number, l: number) => {
            s /= 100;
            l /= 100;
            let c = (1 - Math.abs(2 * l - 1)) * s;
            let x = c * (1 - Math.abs((h / 60) % 2 - 1));
            let m = l - c / 2;
            let r = 0, g = 0, b = 0;
            if (0 <= h && h < 60) { r = c; g = x; b = 0; }
            else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
            else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
            else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
            else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
            else if (300 <= h && h < 360) { r = c; g = 0; b = x; }
            r = Math.round((r + m) * 255);
            g = Math.round((g + m) * 255);
            b = Math.round((b + m) * 255);
            return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        };
        return hslToHex(hue, 70, 70);
    };


    const getChartData = (employee: EmployeeData) => {
        const allDates = new Set<string>();
        employee.projects.forEach(project => {
            project.allocations.forEach(allocation => {
                allDates.add(allocation.date);
            });
        });

        const dates = Array.from(allDates).sort();

        // Pre-compute colors for all projects to avoid state updates during render
        const updatedColorCache = { ...projectColorCache };
        let hasNewColors = false;
        
        employee.projects.forEach(project => {
            if (!updatedColorCache[project.projectName]) {
                updatedColorCache[project.projectName] = getRandomColor();
                hasNewColors = true;
            }
        });

        // Update state only once if new colors were added
        if (hasNewColors) {
            setProjectColorCache(updatedColorCache);
        }

        const series = employee.projects.map(project => {
            const allocationMap = new Map<string, number>();
            project.allocations.forEach(allocation => {
                allocationMap.set(allocation.date, allocation.allocationPercentage);
            });

            const data = dates.map(date => allocationMap.get(date) || 0);

            return {
                label: project.projectName,
                data: data,
                color: updatedColorCache[project.projectName],
                area: true,
                stack: 'total',
                showMark: false
            };
        });

        return { dates, series };
    };

    const filteredEmployees = employeeData.filter(emp =>
        selectedEmployees.includes(emp.idEmployee)
    );

    // Transform data for heatmap visualization (3D view)
    const getHeatmapData = () => {
        if (employeeData.length === 0) return null;

        // Collect all unique dates across all employees
        const allDates = new Set<string>();
        employeeData.forEach(employee => {
            employee.projects.forEach(project => {
                project.allocations.forEach(allocation => {
                    allDates.add(allocation.date);
                });
            });
        });

        const sortedDates = Array.from(allDates).sort();
        if (sortedDates.length === 0) return null;
        
        // Calculate date range in months
        const firstDate = new Date(sortedDates[0]);
        const lastDate = new Date(sortedDates[sortedDates.length - 1]);
        const monthsDiff = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth());
        const yearsDiff = monthsDiff / 12;
        
        // Determine grouping strategy based on date range
        // 0-3 months: daily, 3-12 months: weekly, 1-5 years: biweekly, >5 years: monthly
        let groupingType: 'daily' | 'weekly' | 'biweekly' | 'monthly';
        if (monthsDiff <= 3) {
            groupingType = 'daily';
        } else if (monthsDiff <= 12) {
            groupingType = 'weekly';
        } else if (yearsDiff <= 5) {
            groupingType = 'biweekly';
        } else {
            groupingType = 'monthly';
        }

        // Helper functions for grouping
        const getWeekKey = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = date.getDay();
            const diff = date.getDate() - day; // Get Monday
            const monday = new Date(date.setDate(diff));
            return monday.toISOString().split('T')[0];
        };

        const getBiweekKey = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = date.getDay();
            const diff = date.getDate() - day; // Get Monday
            const monday = new Date(date.setDate(diff));
            // Get week number of year
            const weekNum = Math.floor((monday.getTime() - new Date(monday.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
            const biweekNum = Math.floor(weekNum / 2);
            const biweekStart = new Date(monday.getFullYear(), 0, 1 + (biweekNum * 14));
            return biweekStart.toISOString().split('T')[0];
        };

        const getMonthKey = (dateStr: string) => {
            const date = new Date(dateStr);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
        };

        if (groupingType === 'daily') {
            // Use daily data
            const series = employeeData.map(employee => {
                const employeeAllocations = new Map<string, number>();
                
                employee.projects.forEach(project => {
                    project.allocations.forEach(allocation => {
                        const currentValue = employeeAllocations.get(allocation.date) || 0;
                        employeeAllocations.set(allocation.date, currentValue + allocation.allocationPercentage);
                    });
                });

                const data = sortedDates.map(date => {
                    const value = employeeAllocations.get(date) || 0;
                    return Math.min(value, 100);
                });

                return {
                    name: employee.employeeName,
                    data: data
                };
            });

            return {
                dates: sortedDates.map(d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                series: series
            };
        } else {
            // Group by week, biweek, or month
            const periodMap = new Map<string, string[]>();
            const getKeyFunc = groupingType === 'weekly' ? getWeekKey : groupingType === 'biweekly' ? getBiweekKey : getMonthKey;
            
            sortedDates.forEach(date => {
                const periodKey = getKeyFunc(date);
                if (!periodMap.has(periodKey)) {
                    periodMap.set(periodKey, []);
                }
                periodMap.get(periodKey)!.push(date);
            });

            const periods = Array.from(periodMap.keys()).sort();
            
            const series = employeeData.map(employee => {
                const employeeAllocations = new Map<string, number>();
                
                employee.projects.forEach(project => {
                    project.allocations.forEach(allocation => {
                        const currentValue = employeeAllocations.get(allocation.date) || 0;
                        employeeAllocations.set(allocation.date, currentValue + allocation.allocationPercentage);
                    });
                });

                const periodData = periods.map(periodKey => {
                    const datesInPeriod = periodMap.get(periodKey)!;
                    let totalUtilization = 0;
                    let daysWithData = 0;
                    
                    datesInPeriod.forEach(date => {
                        const value = employeeAllocations.get(date);
                        if (value !== undefined && value > 0) {
                            totalUtilization += Math.min(value, 100);
                            daysWithData++;
                        }
                    });
                    
                    if (daysWithData === 0) return 0;
                    return Math.round((totalUtilization / daysWithData) * 10) / 10;
                });

                return {
                    name: employee.employeeName,
                    data: periodData
                };
            });

            // Format period labels
            const formattedDates = periods.map(p => {
                const periodStart = new Date(p);
                if (groupingType === 'monthly') {
                    return periodStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                } else if (groupingType === 'biweekly') {
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 13);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                } else { // weekly
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 6);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                }
            });

            return {
                dates: formattedDates,
                series: series
            };
        }
    };

    // Transform data for company-wide aggregate heatmap
    const getCompanyHeatmapData = () => {
        if (employeeData.length === 0) return null;

        // Collect all unique dates across all employees
        const allDates = new Set<string>();
        employeeData.forEach(employee => {
            employee.projects.forEach(project => {
                project.allocations.forEach(allocation => {
                    allDates.add(allocation.date);
                });
            });
        });

        const sortedDates = Array.from(allDates).sort();
        if (sortedDates.length === 0) return null;
        
        // Calculate date range in months
        const firstDate = new Date(sortedDates[0]);
        const lastDate = new Date(sortedDates[sortedDates.length - 1]);
        const monthsDiff = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth());
        const yearsDiff = monthsDiff / 12;
        
        // Determine grouping strategy based on date range
        let groupingType: 'daily' | 'weekly' | 'biweekly' | 'monthly';
        if (monthsDiff <= 3) {
            groupingType = 'daily';
        } else if (monthsDiff <= 12) {
            groupingType = 'weekly';
        } else if (yearsDiff <= 5) {
            groupingType = 'biweekly';
        } else {
            groupingType = 'monthly';
        }
        
        // Aggregate utilization by date across all employees
        const dateAggregations = new Map<string, { total: number, count: number }>();
        
        employeeData.forEach(employee => {
            const employeeAllocations = new Map<string, number>();
            
            employee.projects.forEach(project => {
                project.allocations.forEach(allocation => {
                    const currentValue = employeeAllocations.get(allocation.date) || 0;
                    employeeAllocations.set(allocation.date, currentValue + allocation.allocationPercentage);
                });
            });

            employeeAllocations.forEach((value, date) => {
                const existing = dateAggregations.get(date) || { total: 0, count: 0 };
                dateAggregations.set(date, {
                    total: existing.total + Math.min(value, 100),
                    count: existing.count + 1
                });
            });
        });

        if (groupingType === 'daily') {
            // Use daily data
            const data = sortedDates.map(date => {
                const agg = dateAggregations.get(date);
                if (!agg || agg.count === 0) return 0;
                return Math.round((agg.total / agg.count) * 10) / 10;
            });

            return {
                dates: sortedDates.map(d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                series: [{
                    name: intl.get('reports.utilization.companyAverage'),
                    data: data
                }]
            };
        } else {
            // Group by period
            const getWeekKey = (dateStr: string) => {
                const date = new Date(dateStr);
                const day = date.getDay();
                const diff = date.getDate() - day;
                const monday = new Date(date.setDate(diff));
                return monday.toISOString().split('T')[0];
            };

            const getBiweekKey = (dateStr: string) => {
                const date = new Date(dateStr);
                const day = date.getDay();
                const diff = date.getDate() - day;
                const monday = new Date(date.setDate(diff));
                const weekNum = Math.floor((monday.getTime() - new Date(monday.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
                const biweekNum = Math.floor(weekNum / 2);
                const biweekStart = new Date(monday.getFullYear(), 0, 1 + (biweekNum * 14));
                return biweekStart.toISOString().split('T')[0];
            };

            const getMonthKey = (dateStr: string) => {
                const date = new Date(dateStr);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
            };

            const periodMap = new Map<string, string[]>();
            const getKeyFunc = groupingType === 'weekly' ? getWeekKey : groupingType === 'biweekly' ? getBiweekKey : getMonthKey;
            
            sortedDates.forEach(date => {
                const periodKey = getKeyFunc(date);
                if (!periodMap.has(periodKey)) {
                    periodMap.set(periodKey, []);
                }
                periodMap.get(periodKey)!.push(date);
            });

            const periods = Array.from(periodMap.keys()).sort();
            
            const periodData = periods.map(periodKey => {
                const datesInPeriod = periodMap.get(periodKey)!;
                let totalUtilization = 0;
                let daysWithData = 0;
                
                datesInPeriod.forEach(date => {
                    const agg = dateAggregations.get(date);
                    if (agg && agg.count > 0) {
                        totalUtilization += (agg.total / agg.count);
                        daysWithData++;
                    }
                });
                
                if (daysWithData === 0) return 0;
                return Math.round((totalUtilization / daysWithData) * 10) / 10;
            });

            // Format period labels
            const formattedDates = periods.map(p => {
                const periodStart = new Date(p);
                if (groupingType === 'monthly') {
                    return periodStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                } else if (groupingType === 'biweekly') {
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 13);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                } else { // weekly
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 6);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                }
            });

            return {
                dates: formattedDates,
                series: [{
                    name: intl.get('reports.utilization.companyAverage'),
                    data: periodData
                }]
            };
        }
    };

    const heatmapData = getHeatmapData();
    const companyHeatmapData = getCompanyHeatmapData();

    // Configure heatmap chart
    const heatmapOptions = useChart({
        chart: {
            type: 'heatmap',
            toolbar: {
                show: true
            }
        },
        dataLabels: {
            enabled: false
        },
        colors: ACCESSIBLE_COLORS,
        xaxis: {
            type: 'category',
            categories: heatmapData?.dates || [],
            labels: {
                rotate: -45,
                style: {
                    fontSize: '10px'
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontSize: '11px'
                }
            }
        },
        plotOptions: {
            heatmap: {
                colorScale: {
                    ranges: [
                        { from: 0, to: 20, color: '#00D9FF', name: intl.get('reports.utilization.heatmap.low') },
                        { from: 21, to: 50, color: '#90EE90', name: intl.get('reports.utilization.heatmap.medium') },
                        { from: 51, to: 80, color: '#FFB547', name: intl.get('reports.utilization.heatmap.high') },
                        { from: 81, to: 100, color: '#FF4560', name: intl.get('reports.utilization.heatmap.full') }
                    ]
                }
            }
        },
        tooltip: {
            y: {
                formatter: (value: number) => `${value.toFixed(1)}%`
            }
        },
        legend: {
            show: true,
            position: 'bottom'
        }
    });

    // Configure company-wide heatmap chart
    const companyHeatmapOptions = useChart({
        chart: {
            type: 'heatmap',
            toolbar: {
                show: true
            }
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: '11px',
                colors: ['#fff']
            }
        },
        colors: ACCESSIBLE_COLORS,
        xaxis: {
            type: 'category',
            categories: companyHeatmapData?.dates || [],
            labels: {
                rotate: -45,
                style: {
                    fontSize: '10px'
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        plotOptions: {
            heatmap: {
                colorScale: {
                    ranges: [
                        { from: 0, to: 20, color: '#00D9FF', name: intl.get('reports.utilization.heatmap.low') },
                        { from: 21, to: 50, color: '#90EE90', name: intl.get('reports.utilization.heatmap.medium') },
                        { from: 51, to: 80, color: '#FFB547', name: intl.get('reports.utilization.heatmap.high') },
                        { from: 81, to: 100, color: '#FF4560', name: intl.get('reports.utilization.heatmap.full') }
                    ]
                }
            }
        },
        tooltip: {
            y: {
                formatter: (value: number) => `${value.toFixed(1)}% ${intl.get('reports.utilization.averageUtilization')}`
            }
        },
        legend: {
            show: true,
            position: 'bottom'
        }
    });

    const margin = { right: 24 };

    return (
        <Card sx={{ m: 2 }}>
        <Box sx={{ p: 3 }}>
            <Title>{intl.get('mainNavigation.utilization')}</Title>
            <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                <AssignmentAdd /> {intl.get('reports.utilization.title')}
            </Typography>
            <Box>
                <ReportsFilterUtilization
                    handleFilterChange={handleFilterChange}
                    errors={errors}
                    setErrors={setErrors}
                />
                <Stack gap={4} sx={{ mt: 4 }}>
                    <PermissionsGate
                        identifier="ui.reports.utilization.gate"
                        viewRoles={[]}
                        editRoles={[]}
                    >
                        {loading &&
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ mb: 4 }}>
                                    <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                </Box>
                                <Box>
                                    <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
                                    <Skeleton variant="rectangular" width="100%" height={400} />
                                </Box>
                            </Box>
                        }
                        
                        {/* Company-wide average utilization heatmap */}
                        {!loading && employeeData.length > 0 && companyHeatmapData && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    {intl.get('reports.utilization.companyHeatmap.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {intl.get('reports.utilization.companyHeatmap.description')}
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Chart
                                        type="heatmap"
                                        series={companyHeatmapData.series}
                                        options={companyHeatmapOptions}
                                        height={120}
                                    />
                                </Paper>
                            </Box>
                        )}
                        
                        {/* Show heatmap when viewing all employees (none specifically selected from filter) */}
                        {!loading && employeeData.length > 0 && filteredEmployees.length > 5 && heatmapData && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    {intl.get('reports.utilization.heatmap.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {intl.get('reports.utilization.heatmap.description')}
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Chart
                                        type="heatmap"
                                        series={heatmapData.series}
                                        options={heatmapOptions}
                                        height={Math.max(400, employeeData.length * 30)}
                                    />
                                </Paper>
                            </Box>
                        )}
                        
                        {/* Show individual employee details for selected employees */}
                        {!loading && filteredEmployees.length > 0 && filteredEmployees.length <= 5 && (
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell><b>{intl.get('reports.utilization.employee')}</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredEmployees.map((employee) => {
                                            const isExpanded = expandedEmployees.includes(employee.idEmployee);
                                            const chartData = getChartData(employee);
                                            return (
                                                <React.Fragment key={employee.idEmployee}>
                                                    <TableRow>
                                                        <TableCell>
                                                            <IconButton
                                                                aria-label="expand row"
                                                                size="small"
                                                                onClick={() => handleEmployeeToggle(employee.idEmployee)}
                                                            >
                                                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell scope="row">
                                                            <b>{employee.employeeName}</b>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={2} sx={{ p: 0 }}>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                <Box sx={{ p: 2 }}>
                                                                    <Box sx={{ width: '100%', height: 370 }}>
                                                                        <LineChartWrapper
                                                                            height={350}
                                                                            series={chartData.series}
                                                                            xAxis={[
                                                                                {
                                                                                    scaleType: 'point',
                                                                                    data: chartData.dates.map(d => new Date(d).toLocaleDateString()),
                                                                                    tickLabelStyle: {
                                                                                        angle: -45,
                                                                                        textAnchor: "end",
                                                                                      },
                                                                                    height: 70,
                                                                                },
                                                                            ]}
                                                                            yAxis={[
                                                                                {
                                                                                    width: 50,
                                                                                    min: 0,
                                                                                    max: 100,
                                                                                    label: intl.get('reports.utilization.allocation'),
                                                                                    valueFormatter: (value) => `${value}%`
                                                                                }
                                                                            ]}
                                                                            sx={{
                                                                                [`& .${lineElementClasses.root}`]: {
                                                                                    display: 'none',
                                                                                },
                                                                            }}
                                                                            margin={margin}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {!loading && employeeData.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {intl.get('reports.utilization.selectEmployee')}
                                </Typography>
                            </Box>
                        )}
                    </PermissionsGate>
                </Stack>
            </Box>
        </Box>
        </Card>
    );
}

export default UtilizationReportComponent;